import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { pick } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import {
  AcceptedResourceItemType,
  AnyResource,
  ApprovalLog,
  Breadcrumb,
  CommunityStatus,
  Country,
  PriceConfiguration,
  ResourceType,
  SystemNotificationList,
  UserSettings,
} from '@models';
import {
  AppEvent,
  AppEventType,
  EventQueueService,
  UserBeanService,
} from '@services';
import {
  deleteEnablebyList as _deleteEnablebyList,
  addFromDayToDayToEvent,
  defineApproval,
  defineVersionApproval,
  downloadEnablebyList,
  escapeHtml,
  exportEnablebyList,
  formatSizeUnits,
  generateAnnotationId,
  getCategoryIconColor,
  getMaxExpireDateTime,
  getParentTypeByResource,
  getPathByType,
  getResourceTypeByList,
  getResourceTypeUrl,
  getTodayTime,
  getZoomLevel,
  isAndroidDevice,
  isAppleDevice,
  isEditable,
  isImageFile,
  isMobileDevice,
  isOnlyFormatList,
  isSet,
  isWritableResource,
  ownersByList,
  prettifyImageLabel,
  resetTimeToDate,
  truncate,
  viewersByList,
} from '@shared/utils';
import {
  getDownloadZipUrl as _getDownloadZipUrl,
  buildFileURL,
  buildResourceURL,
  getDownloadMergeUrl,
  getDownloadSignedUrl,
  getDownloadUrl,
  getDownloadUrlWithAnnotations,
  getEventExcelExportUrl,
  getEventIcsExportUrl,
  getSystemConditionsDownloadUrl,
  getUrlConfig,
  getUrlPost,
} from '@shared/utils/resource-url';
import { AppState } from '@store';
import { API_URL } from '../constants';
import { ModalService } from './modal.service';

@Service()
export class UtilitiesService {
  http = inject(HttpClient);
  translate = inject(TranslateService);
  eventQueueService = inject(EventQueueService);
  router = inject(Router);
  store = inject<Store<AppState>>(Store);
  modalService = inject(ModalService);
  userBeanService = inject(UserBeanService);

  imageVersions = new Map<number, number>();
  userBean!: UserSettings;
  areZippedFilenamesShortened!: boolean;

  constructor() {
    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.userBean = state.user;
        this.areZippedFilenamesShortened =
          state.settings?.areZippedFilenamesShortened;
      });
  }

  defineApproval = defineApproval;
  defineVersionApproval = defineVersionApproval;
  addFromDayToDayToEvent = addFromDayToDayToEvent;
  resetTimeToDate = resetTimeToDate;
  getTodayTime = getTodayTime;
  getMaxExpireDateTime = getMaxExpireDateTime;
  formatSizeUnits = formatSizeUnits;
  getPathByType = getPathByType;
  isWritableResource = isWritableResource;
  getParentTypeByResource = getParentTypeByResource;
  buildResourceURL = buildResourceURL;
  getDownloadUrl = getDownloadUrl;
  getDownloadSignedUrl = getDownloadSignedUrl;
  getDownloadUrlWithAnnotations = getDownloadUrlWithAnnotations;
  buildFileURL = buildFileURL;
  getDownloadMergeUrl = getDownloadMergeUrl;
  getEventIcsExportUrl = getEventIcsExportUrl;
  getEventExcelExportUrl = getEventExcelExportUrl;
  isImageFile = isImageFile;
  getUrlConfig = getUrlConfig;
  getUrlPost = getUrlPost;
  prettifyImageLabel = prettifyImageLabel;
  getZoomLevel = getZoomLevel;
  isAppleDevice = isAppleDevice;
  isAndroidDevice = isAndroidDevice;
  isMobileDevice = isMobileDevice;
  getCategoryIconColor = getCategoryIconColor;
  isEditable = isEditable;
  viewersByList = viewersByList;
  ownersByList = ownersByList;
  isOnlyFormatList = isOnlyFormatList;
  downloadEnablebyList = downloadEnablebyList;
  exportEnablebyList = exportEnablebyList;
  getResourceTypeUrl = getResourceTypeUrl;
  getResourceTypeByList = getResourceTypeByList;
  generateAnnotationId = generateAnnotationId;
  getSystemConditionsDownloadUrl = getSystemConditionsDownloadUrl;

  getDownloadZipUrl = (rids: number | { rid: number } | { rid: number }[]) =>
    _getDownloadZipUrl(rids, this.areZippedFilenamesShortened);

  deleteEnablebyList = (
    list: {
      permission: number;
      authorId?: number;
      type: ResourceType;
      subtype?: number;
    }[]
  ) =>
    _deleteEnablebyList(list, {
      userId: this.userBean.userId,
      editorDeletePermission: this.userBean.editorDeletePermission,
    });

  updateUserImage = (rid: number, image?: string) => {
    this.imageVersions.set(rid, (this.imageVersions.get(rid) ?? 0) + 1);

    this.eventQueueService.dispatch(
      new AppEvent(AppEventType.LOAD_PROFILE_IMAGE, { rid, image })
    );
  };

  getUserImageVersion = (rid: number) => this.imageVersions.get(rid) ?? 0;

  getPathbyId = (rid: number, type: ResourceType) => {
    if (isSet(rid) && isSet(type)) {
      const url = `${API_URL}/${getPathByType(type)}/${rid}`;

      return firstValueFrom(this.http.get<AnyResource>(url)).then(
        (resourceObj) => {
          if (resourceObj) {
            resourceObj.breadcrumb.push(
              pick(resourceObj, [
                'description',
                'displayName',
                'image',
                'imageDefined',
                'permission',
                'rid',
                'shortName',
                'subtype',
                'type',
              ]) as Breadcrumb
            );

            return resourceObj.breadcrumb;
          }

          return null;
        },
        () => null
      );
    } else {
      return null;
    }
  };

  linkHTML = (
    resource:
      | Nullable<{
          rid?: number;
          displayName?: string;
          type?: ResourceType;
          subtype?: number;
          deleted?: boolean;
          pid?: number;
        }>
      | undefined,
    isDisabled = false
  ) => {
    if (!resource) {
      return '';
    }

    if (isSet(resource.displayName) && resource.displayName !== '') {
      const hrefMap: Partial<Record<ResourceType, string>> = {
        [ResourceType.USER]: `/users/${resource.rid}`,
        [ResourceType.GROUP]: `/groups/${resource.rid}`,
        [ResourceType.ROOM]: `/rooms/${resource.rid}`,
        [ResourceType.EVENT]: `/events/${resource.rid}`,
        [ResourceType.FILE]: `/home?file=${resource.rid}`,
        [ResourceType.DISCUSSION]: `/home?discussion=${resource.rid}`,
        [ResourceType.VOTING]: `/home?vote=${resource.rid}`,
      };

      const href = resource.type
        ? hrefMap[resource.type]
        : `/users/${resource.rid}`;
      const className =
        isDisabled || isSet(resource.deleted)
          ? 'disabled_link'
          : 'link-tooltip ';

      const text = escapeHtml(truncate(resource.displayName ?? '', 30));

      return href ? `<a class="${className}" href="${href}">${text}</a>` : text;
    }

    const labelMap: Partial<Record<ResourceType, string>> = {
      [ResourceType.COMMUNITY]: 'WEB_COMMUNITY_SETTINGS',
      [ResourceType.USER]: 'WEB_USER_UNKNOW',
      [ResourceType.GROUP]: 'WEB_GROUP_UNKNOW',
      [ResourceType.ROOM]: 'WEB_ROOM_UNKNOW',
      [ResourceType.FILE]: 'WEB_FILE_UNKNOW',
      [ResourceType.DISCUSSION]: 'WEB_DISCUSSION_UNKNOW',
      [ResourceType.EVENT]: 'WEB_EVENT_UNKNOW',
      [ResourceType.REQUEST]: 'WEB_REQUEST_UNKNOW',
    };

    const key =
      resource.subtype === ResourceType.USER && !isSet(resource.displayName)
        ? ResourceType.USER
        : resource.type;

    return key ? this.translate.instant(labelMap[key] ?? '') || '' : '';
  };

  getResourceConfig = (
    pid: number,
    type: ResourceType,
    parentType: ResourceType
  ) =>
    this.http.get<Record<string, unknown>>(
      `${API_URL}/${getUrlConfig(pid, type, parentType)}`
    );

  getCreationConfig = <T = Record<string, unknown>>(
    parentRid: Nullable<number>,
    resourceType: ResourceType,
    parentType: Nullable<ResourceType>
  ) => {
    const url = getUrlConfig(parentRid, resourceType, parentType);

    if (!url) {
      return null;
    }

    return firstValueFrom(this.http.get<T>(`${API_URL}/${url}`));
  };

  redirectPage = (to: string, options?: NavigationExtras) => {
    if (to === '/login' || to?.startsWith('/login')) {
      this.modalService.closeAll();
    }

    if (
      this.userBean.communityStatus === CommunityStatus.SUSPENDED &&
      this.userBean.billing
    ) {
      this.router.navigate(['/billing']);

      return;
    }

    const extractedQuery = this.router.parseUrl(to).queryParams;
    const basePath = to.split('?')[0];

    this.router.navigate([basePath || '/home'], {
      queryParamsHandling: 'merge',
      queryParams: {
        ...extractedQuery,
        ...options?.queryParams,
      },
      ...options,
    });
  };

  getSystemNotifications = () =>
    firstValueFrom(
      this.http.get<SystemNotificationList[]>(`${API_URL}/system/notifications`)
    );

  renameResource = (resource: {
    rid: number;
    type: ResourceType;
    name?: string;
    updateDate?: number;
  }) =>
    firstValueFrom(
      this.http.put<AcceptedResourceItemType>(
        `${API_URL}/${getPathByType(resource.type)}/${resource.rid}`,
        resource
      )
    ).then(
      (newResource) => newResource,
      (error: { message?: string; status?: number }) => error
    );

  getFileApprovallog = (fileRid: number | number[], fileVersion?: number) => {
    const ridParam = Array.isArray(fileRid) ? fileRid.join('&rid=') : null;
    const url = `${API_URL}/${
      fileVersion
        ? `files/${fileRid}/versions/${fileVersion}/approvallog`
        : ridParam
          ? `files/approvallog?rid=${ridParam}`
          : `files/${fileRid}/approvallog`
    }`;

    return firstValueFrom(this.http.get<ApprovalLog>(url));
  };

  getEIamPortalUrl = () =>
    firstValueFrom(
      this.http.get<{ url?: string }>(`${API_URL}/system/ssosettings`)
    );

  getSystemSettings = () =>
    this.http.get<Record<string, unknown>>(`${API_URL}/system/settings`);

  getSystemCountries = (locale: string) =>
    this.http.get<Country[]>(`${API_URL}/system/countries?lang=${locale}`);

  getSystemPrices = () =>
    this.http.get<PriceConfiguration>(
      `${API_URL}/system/prices?authenticated=${!!this.userBeanService.user?.userId}`
    );

  searchResources = (searchParams: Record<string, unknown>) =>
    this.http.post<AnyResource[]>(`${API_URL}/search`, searchParams);

  getScheduledNotifications = <
    T = {
      notificationId: number;
      date: number;
      text: string;
      startDate?: number;
      group?: string;
    },
  >(
    resourceType: ResourceType,
    rid: number
  ) =>
    this.http.get<T[]>(
      `${API_URL}/${getPathByType(resourceType)}/${rid}/schedulednotifications`
    );

  getBuildVersion = () =>
    this.http.get<{ buildnum: string }>('/assets/build-version.json');
}
